// Production namespace — RUSSIAN (Module 04 §4, slice 04-A). The design partner
// is a cable factory in Almaty (docs/context/kazakhstan-group.md) whose
// planners create and release work orders, whose storekeepers issue material
// against them, and whose finance closes them — all in Russian. An English
// dispatch board in front of a Russian-speaking planner is an unusable one.
//
// Typed `Translated<typeof productionEn>` (src/i18n-shape.ts), so a key added
// to or removed from `productionEn` fails `tsc` here. Values are checked
// separately, at runtime, by tools/ci/i18n-parity.ts.
//
// ── REGISTER ────────────────────────────────────────────────────────────────
// Plant-office and production-accounting Russian, not calque-from-English. The
// choices worth arguing with, in one place:
//
//  · release      — «выдать в цех» (выдача заказа в производство), the word a
//                   Russian planning office actually uses. NOT «релиз», NOT
//                   SAP-RU's «деблокировать», which nobody says aloud.
//  · traveler     — «маршрутный лист», the paper that travels with the batch.
//  · backflush    — «автосписание» (1С's own word for automatic consumption).
//  · dispatch     — «диспетчеризация»/«диспетчерская доска»; the board title is
//                   «Диспетчерская доска» — the room it replaces is called
//                   диспетчерская.
//  · scrap reason — «причина брака», matching module 05's «причина» so the two
//                   grids read alike.
//  · overhead     — «накладные расходы», the accounting term.
//  · NRV          — «ЧЦР (чистая цена реализации)» spelled out once and
//                   abbreviated nowhere: the outputs panel has room, and an
//                   unexplained abbreviation is jargon, not translation.
//  · statuses     — «выдан в цех / в работе / завершён / закрыт / отменён» for
//                   the order; operation statuses are FEMININE («завершена»)
//                   because «операция» is.
//
// ── WHAT IS NOT HERE ────────────────────────────────────────────────────────
// The entity nouns. «Заказ на производство», «Рабочий центр», «Партия»,
// «Спецификация» come from the terminology layer (TERMINOLOGY_BASE.ru, module
// 02 §2.5), which the tenant may override — the cable pack does.

import type { ProductionDict } from "./production.i18n.js";

export const productionRuDict: ProductionDict = {
  nav: {
    aria: "Разделы производства",
    dispatch: "Диспетчерская доска",
    conflicts: "Конфликты",
    devices: "Устройства",
    calibration: "Калибровка",
    shifts: "Смены",
    blocked: "Простои",
    // «Сдельное количество», а не «сдельная оплата»: запись — это КОЛИЧЕСТВО,
    // и название по деньгам обещало бы расчётный листок, которого в этом
    // выпуске нет (§12 Q1).
    pieceRate: "Сдельное количество",
  },

  salesOrder: {
    hidden: "Заказ клиента",
    line: "строка {{line}}",
  },

  common: {
    retry: "Повторить",
    requestId: "Идентификатор запроса",
    loading: "Загрузка…",
    save: "Сохранить",
    saving: "Сохранение…",
    cancel: "Отмена",
    close: "Закрыть",
    confirm: "Подтвердить",
    back: "Назад",
    actions: "Действия",
    notAvailable: "—",
    priorityLabel: "Приоритет {{value}}",
    overdue: "Просрочен",
  },

  status: {
    wo: {
      draft: "Черновик",
      released: "Выдан в цех",
      in_progress: "В работе",
      completed: "Завершён",
      closed: "Закрыт",
      cancelled: "Отменён",
    },
    op: {
      queued: "В очереди",
      ready: "К запуску",
      in_progress: "В работе",
      paused: "Приостановлена",
      completed: "Завершена",
      skipped: "Пропущена",
    },
  },

  costType: {
    material: "Материалы",
    labor: "Трудозатраты",
    overhead: "Накладные расходы",
    waste_variance: "Отклонение по отходам",
  },

  issueMethod: {
    scan: "По сканированию",
    backflush: "Автосписание",
    manual: "Вручную",
    remnant_match: "Подобрано из остатков",
  },

  captureSource: {
    office: "Введено в офисе",
    floor: "Зафиксировано в цеху",
  },

  outputKind: {
    finished: "Готовая продукция",
  },

  list: {
    createButton: "Создать заказ на производство",
    col: {
      number: "Номер",
      progress: "Выполнение",
      status: "Статус",
      due: "Срок",
      costToDate: "Затраты на сегодня",
      salesOrder: "Заказ клиента",
    },
    progressOf: "{{good}} из {{planned}}",
    filter: {
      status: "Статус",
      any: "Любой",
      anyFacility: "Любая",
      from: "С",
      to: "По",
      overdueOnly: "Только просроченные",
      search: "Поиск",
      searchPlaceholder: "Номер или продукт…",
      sort: "Сортировка",
      sortDue: "По сроку",
      sortPriority: "По приоритету",
      sortCreated: "По дате создания",
      clear: "Сбросить фильтры",
      filters: "Фильтры",
      filtersCount: "Фильтры ({{n}})",
      done: "Готово",
      remove: "Убрать: {{label}}",
    },
    chips: {
      overdue: "Просроченные",
      inProgress: "В работе",
      awaitingClose: "Ожидают закрытия",
    },
    emptyTitle: "Заказов на производство пока нет",
    emptyBody:
      "Создайте заказ вручную или запланируйте его из производственной строки заказа клиента.",
    emptyBodyReadOnly:
      "Заказы на производство появятся здесь, когда планировщик их создаст и выдаст в цех.",
    emptyTitleOwnBook: "По вашим заказам пока нет заказов на производство",
    emptyBodyOwnBook:
      "Здесь собраны заказы на производство, запланированные под ваши заказы и под закреплённых за вами клиентов. Страница каждого заказа тоже показывает его заказы на производство.",
    emptyFilteredTitle: "По этим фильтрам ничего не найдено",
    emptyFilteredBody: "Ослабьте или сбросьте фильтры, чтобы увидеть больше.",
    error: "Не удалось загрузить заказы на производство",
    offlineTitle: "Нет сети, поэтому список приостановлен",
    offlineBody: "Он заполнится сам, как только связь восстановится.",
    loadMore: "Показать ещё",
  },

  detail: {
    error: "Не удалось загрузить заказ",
    backToList: "К списку",
    tabsAria: "Разделы заказа",
    tab: {
      operations: "Операции",
      materials: "Материалы",
      costs: "Затраты",
      calibration: "Калибровка",
      genealogy: "Генеалогия",
      events: "События",
    },
    meta: {
      due: "Срок",
      priority: "Приоритет",
      quantity: "Количество",
      notes: "Примечания",
      created: "Создан",
      cancelReason: "Причина отмены",
      salesOrder: "Заказ клиента",
    },
    action: {
      edit: "Изменить черновик",
      release: "Выдать в цех",
      complete: "Завершить",
      close: "Закрыть",
      cancel: "Отменить",
      editPriority: "Изменить приоритет",
      traveler: "Маршрутный лист (PDF)",
      mobileHint: "Смена состояния с телефона отключена — используйте компьютер.",
    },
    // D-299, решение R10: отказ в закрытии — из структурированных данных
    // отказа, по строке на причину; ссылка — тому, кто может её устранить,
    // остальным — кто может. Без номеров разделов спецификации и маршрутов.
    closeBlocked: {
      downtime: "По операциям заказа остаются открытые простои: {{count}}.",
      downtimeSince: "Остановлено с {{times}}.",
      downtimeLink: "Завершить на доске остановленных рабочих центров",
      downtimeWho:
        "Завершить простой может администратор площадки или группы на доске остановленных рабочих центров, а также планшет участка, пока на нём идёт работа.",
      conflicts: "По заказу остаются открытые конфликты синхронизации: {{count}}.",
      conflictsLink: "Разобрать в очереди конфликтов синхронизации",
      conflictsWho:
        "Конфликты разбирает администратор площадки или группы — в очереди конфликтов синхронизации.",
      handoffs: "По заказу остаются нерешённые передачи брака: {{count}}.",
      handoffsLink: "Решить на табло смены",
      handoffsWho:
        "Решение по передачам брака принимает администратор площадки или группы либо ОТК — на табло смены.",
    },
    editForm: {
      title: "Изменить черновик",
      titlePriority: "Изменить приоритет",
      qtyPlanned: "Плановое количество",
      dueDate: "Срок",
      priority: "Приоритет",
      notes: "Примечания",
      priorityHint: "Приоритет можно менять до начала работ; остальное — только в черновике.",
    },
    cancelForm: {
      title: "Отменить заказ",
      reason: "Причина",
      dispositionTitle: "Что делать с уже произведённым годным количеством?",
      receiveQty: "Принять на склад",
      scrapQty: "В брак",
      scrapReason: "Причина брака",
      dispositionHint:
        "Всё произведённое, но не принятое, должно быть либо принято, либо списано в брак — одним шагом, чтобы не осталось бесхозной стоимости.",
      issuedBlockedNote:
        "Материал всё ещё числится за этим заказом. Верните его на склад из цеха или отрицательной выдачей либо спишите здесь.",
      confirm: "Отменить заказ",
    },
    ops: {
      colSeq: "Шаг",
      colName: "Название",
      colStatus: "Статус",
      colQty: "Годных / брак",
      colPlanned: "План",
      colActual: "Факт",
      colSetupMin: "Наладка, мин",
      colRunMin: "Работа, мин",
      colMachineMin: "Машинное время, мин",
      skippedReason: "Пропущена: {{reason}}",
      skip: "Пропустить",
      skipTitle: "Пропустить этот шаг",
      reworkBadge: "Переделка",
      qcHoldBadge: "Задержано ОТК",
      releaseHold: "Снять задержку",
      releaseHoldTitle: "Снять задержку ОТК",
      releaseHoldHint:
        "Снятие задержки отменяет решение ОТК. Причина записывается в журнал аудита и не редактируется.",
      releaseHoldNote: "Причина",
      skipReason: "Причина",
      skipHint: "Причина — постоянная запись о том, почему плановый шаг не выполнялся.",
      record: "Внести результат",
      recordTitle: "Внести результат",
      qtyGood: "Годное количество",
      qtyScrap: "Количество брака",
      qtyInvalid: "Количество — десятичное число, не более трёх знаков после запятой.",
      scrapReason: "Причина брака",
      pickReason: "Выберите причину…",
      scrapReasonRequired: "При любом браке причина обязательна.",
      startedAt: "Начало",
      endedAt: "Окончание",
      timesHint:
        "Укажите оба времени, чтобы распределить машинные накладные расходы; оставьте пустыми, если время никто не фиксировал.",
      complete: "Шаг закончен — отметить завершённым",
      note: "Примечание",
      submit: "Внести",
      stoppageEndBeforeWindow:
        "Ничего не сохранено: открытый простой шага закончился бы более 24 часов назад. Укажите более позднее время окончания или завершите простой на доске остановленных рабочих центров.",
      recorded: "Внесено.",
      reportsTitle: "Внесённые результаты",
      reportsEmpty: "Результатов пока нет.",
      colSource: "Как внесено",
      colAt: "Когда",
      calTitle: "Замер бухты (этот шаг требует контрольного замера)",
      calHint:
        "В себестоимости и на складе используется значение весов; показание станка только сравнивается с ним.",
      calBatch: "Метка бухты / партии",
      calClaimed: "Показание станка",
      calMeasured: "Измерено",
      calUom: "Единица",
      calMethod: "Способ замера",
      calScan: "Скан выходной бухты (необязательно)",
      calScanHint:
        "QR-код или серийный номер складской единицы, на которую легла партия. Оставьте пустым, если бухта ещё не промаркирована.",
      calRequired:
        "Этот шаг требует контрольного замера: укажите метку бухты, показание станка и измеренное значение — либо внесите результат без завершения шага.",
      calQtyInvalid:
        "Показание станка и измеренное значение — положительные десятичные числа не более чем с тремя знаками после запятой.",
      // §3.3 шаг 8 из бумажного маршрутного листа (D-085 п. 3, 04-C-3).
      // Формулировки направления — те же, что на планшете
      // (`production.floor.op.serialDirections`): один и тот же физический
      // факт не должен описываться двумя словарями.
      serialTitle: "Серийные номера из маршрутного листа (этот шаг требует фиксации серийника)",
      serialHint:
        "Заполните по строке на каждую единицу из маршрутного листа: код скана, выгравированный номер или оба.",
      serialAlreadyCaptured:
        "Серийный номер по этому шагу уже зафиксирован в цеху. Добавьте строку только для единицы, пропущенной планшетом.",
      serialRow: "Промаркированная единица {{index}}",
      serialRemove: "Удалить",
      serialRemoveRow: "Удалить промаркированную единицу {{index}}",
      serialDirection: "В какую сторону?",
      // BYTE-IDENTICAL TO THE FLOOR'S OWN STRINGS below, and that is the whole
      // point: the comment above says one physical fact must not be described
      // in two vocabularies, and until 2026-08-06 that was true in English and
      // FALSE in Russian — the one locale KazKabel actually runs in. A planner
      // reading "Снято" on the office form and an operator reading "Сошло" on
      // the tablet cannot tell they are recording the same event, which is
      // exactly the confusion the shared wording exists to prevent. If either
      // side is reworded, reword both.
      serialDirections: {
        produced: "Сошло с этого станка",
        consumed: "Пошло в этот станок",
      },
      serialDirectionNote: {
        produced: "Записывается то, что операция ВЫПУСТИЛА, — именно это нужно для завершения.",
        consumed: "Записывается то, что операция ИЗРАСХОДОВАЛА, — входная сторона цепочки.",
      },
      serialScan: "Код скана с этикетки",
      serialNo: "Выгравированный серийный номер",
      serialQty: "Измеренная длина / масса",
      serialUom: "Единица",
      serialAdd: "Добавить ещё одну единицу",
      serialAddHint:
        "Добавьте строку, если одна запись маршрутного листа промаркировала несколько единиц, например барабаны из одного прогона.",
      serialRequired:
        "Этот шаг требует серийного номера: укажите код скана или гравировку хотя бы одной единицы либо внесите результат без завершения шага.",
      serialProblem: {
        nothing_identified:
          "Промаркированная единица {{index}}: нужен код скана или выгравированный номер — одно из двух опознаёт единицу.",
        qty_required: "Промаркированная единица {{index}}: не указана измеренная длина или масса.",
        qty_invalid:
          "Промаркированная единица {{index}}: измеренная длина или масса — положительное десятичное число не более чем с тремя знаками после запятой.",
        uom_required: "Промаркированная единица {{index}}: не указана единица измерения.",
      },
    },
    // ---- Вкладка «Генеалогия» (§4 S2, срез 04-C-3) ------------------------
    genealogy: {
      consumedLabel: "Израсходовано",
      producedLabel: "Произведено",
      consumedTitle: "Вошло в этот заказ",
      producedTitle: "Вышло из этого заказа",
      consumedBody:
        "Партии и единицы, отсканированные рабочим на входе в производство, — входная сторона цепочки прослеживаемости, по которой ведётся отзыв продукции.",
      producedBody:
        "Маркированные единицы и выгравированные серийные номера, полученные в ходе выпуска. При завершении заказа измеренные количества здесь имеют приоритет над нормой из спецификации.",
      consumedNone: "По этому заказу вход не сканировался.",
      producedNone: "По этому заказу пока ничего не промаркировано.",
      emptyPending:
        "Серийные номера и партии пока не зафиксированы. Оператор внесёт их до завершения шага с фиксацией серийного номера.",
      emptyNotRequired:
        "Ни один шаг этого заказа не фиксирует серийные номера или партии, поэтому генеалогии нет.",
      colIdentity: "Идентификатор",
      colProduct: "Продукт",
      colQty: "Количество",
      colStep: "Операция",
      colWhen: "Зафиксировано",
      colSource: "Внёс",
      noProduct: "Единица не зарегистрирована — только гравировка",
      openUnit: "Открыть единицу",
      openTrace: "Проследить цепочку",
      source: {
        floor: "Устройство в цеху",
        office: "Офис (маршрутный лист)",
      },
    },

    // ---- Вкладка «События» (§4 S2 — §5 e10 / §2.3; построена 2026-08-08) ---
    events: {
      error: "Не удалось загрузить ленту событий",
      emptyTitle: "Событий пока нет",
      emptyBody:
        "С устройств пока ничего не записано. Результаты, введённые в офисе, — на вкладке «Операции».",
      emptyFilteredTitle: "Событий такого вида нет",
      emptyFilteredBody:
        "События по заказу есть, но не выбранного вида — сбросьте фильтр, чтобы увидеть все.",
      filterKind: "Вид",
      filterAll: "Все виды",
      timezoneNote: "Время указано по часам площадки ({{timezone}}).",
      timezoneNoteFallback:
        "Часовой пояс площадки недоступен — время показано по часам вашего браузера.",
      loadMore: "Показать ещё",
      colWhen: "Когда",
      colKind: "Событие",
      colDetail: "Подробности",
      colSource: "Источник записи",
      recordedAt: "Записано {{when}}.",
      staleFlag:
        "Часы устройства расходились на {{hours}} ч, когда пришло событие. Оно записано и помечено на разбор, а к смене отнесено по времени получения.",
      clampedFlag:
        "Часы устройства спешили, поэтому событие отнесено к смене по времени его получения.",
      kind: {
        op_started: "Шаг начат",
        op_paused: "Шаг приостановлен",
        op_resumed: "Шаг возобновлён",
        op_completed: "Шаг завершён",
        qty_reported: "Количество зафиксировано",
        material_issued: "Материал выдан",
        material_returned: "Материал возвращён",
        downtime_started: "Простой начался",
        downtime_ended: "Простой закончился",
        calibration_recorded: "Записан замер барабана",
        serial_lot_captured: "Зафиксирован серийный номер / партия",
        defect_handoff_flagged: "Брак отмечен при передаче",
        correction: "Корректировка количества",
      },
      source: {
        correction: "Офисная корректировка (разбор конфликта)",
      },
      detail: {
        qty: "{{good}} годных / {{scrap}} брака",
        issued: "Выдано {{qty}} {{uom}}",
        returned: "Возвращено на склад {{qty}} {{uom}}",
        calibration: "Станок заявил {{claimed}}, замер {{measured}} {{uom}}",
        defect: "{{qty}} с браком · {{reason}}",
        downtimeReason: "Причина: {{reason}}",
        correctionRef: "Исправляет событие, разобранное в конфликте",
      },
    },

    materials: {
      colPlanned: "План",
      colReserved: "Зарезервировано",
      notReserved: "Не зарезервировано: {{qty}} {{uom}}",
      notReservedHint:
        "Часть плана не выдана и не зарезервирована: первая выдача закрыла резерв, либо припуск на брак добавлен после выпуска заказа.",
      colIssued: "Выдано",
      colVariance: "Отклонение",
      colCost: "Стоимость выданного",
      colMethod: "Как",
      issue: "Выдать материал",
      issueTitle: "Выдача материала",
      qty: "Количество",
      uom: "Единица",
      forStep: "На шаг",
      noStep: "Весь заказ (без шага)",
      submit: "Выдать",
      issued: "Выдано.",
      qtyPositive: "Количество должно быть больше нуля.",
      empty: "Компоненты не запланированы — в закреплённой спецификации нет строк.",
      // 04-B-4 (D-076/D-081): действие возврата — заменило заметку
      // «возвраты появятся с 04-B».
      historyTitle: "Выдачи и возвраты",
      historyEmpty: "Ещё ничего не выдано.",
      colWhen: "Когда",
      colRemaining: "К возврату",
      methodLabel: {
        manual: "Вручную",
        scan: "Скан в цеху",
        backflush: "Автосписание",
        remnant_match: "Подбор остатка",
      },
      returnRow: "Возврат ранее выданного",
      returnAction: "Вернуть",
      returnTitle: "Возврат на склад",
      returnQty: "Количество к возврату",
      returnHint:
        "Возврат создаёт собственное складское движение по зафиксированной стоимости исходной выдачи — сама выдача остаётся в истории.",
      returnExceeds: "Это больше, чем осталось выданным по этой строке.",
      returnSubmit: "Вернуть на склад",
      returned: "Возвращено.",
      returnAfterCompletionHint:
        "Этот заказ завершён. До закрытия он принимает возврат оставшегося материала, но новых выдач не принимает.",
      returnClosedWayOn:
        "Ничего не возвращено: заказ закрыли раньше, а закрытый заказ не принимает материал обратно. Оприходуйте материал корректировкой остатков в разделе «Склад».",
      returnAfterCloseHint:
        "Заказ закрыт и не принимает материал обратно. Оприходуйте остаток корректировкой остатков в разделе «Склад».",
      returnUnitNotAvailable:
        "Ничего не возвращено: исходный контейнер сейчас не может принять материал (задержан, в пути или только что опустошён). Повторите, когда его освободят.",
    },
    costs: {
      totalsTitle: "Затраты на сегодня",
      pendingLabor:
        "Трудозатраты не зафиксированы: у результатов из офиса нет таймера. Трудозатраты приходят от цеховых таймеров или ручной корректировки.",
      ledgerTitle: "Проводки",
      colType: "Вид",
      colSource: "Источник",
      colAmount: "Сумма",
      colAt: "Внесена",
      ledgerEmpty: "Проводок пока нет.",
      loadMore: "Показать ещё",
      source: {
        material_issue: "Выдача материала",
        labor_time_entry: "Учёт рабочего времени",
        machine_time: "Машинное время",
        calibration_variance: "Расхождение калибровки",
        scrap_beyond_standard: "Брак сверх нормы",
        manual_adjustment: "Ручная корректировка",
      },
      outputsTitle: "Выходы",
      colKind: "Вид",
      colRatio: "Коэффициент",
      colShare: "Доля",
      colProduced: "Произведено",
      colNrv: "Чистая цена реализации",
      noNrv: "Источника цены для этой побочной продукции пока нет. Она не оценена — это не ноль.",
      adjust: "Ручная корректировка",
      adjustTitle: "Внести ручную корректировку",
      adjustType: "Вид затрат",
      adjustAmountIn: "Сумма ({{currency}}, в минорных единицах, со знаком)",
      adjustScale: "Целое число минорных единиц {{currency}} — отрицательное для кредита.",
      adjustPreview: "Будет проведено как {{amount}}.",
      adjustAmountInvalid: "Сумма — целое число в минорных единицах (отрицательная — для кредита).",
      adjustReason: "Причина",
      adjustHint:
        "Журнал ведётся только на добавление: исправление — это компенсирующая проводка, а не правка.",
      adjustSubmit: "Провести",
      adjusted: "Проведено.",
    },
  },

  createModal: {
    stepCreate: "Создание",
    stepRelease: "Выдача в цех",
    searchLabel: "Найти продукт",
    searchPlaceholder: "Поиск по артикулу или названию…",
    noProducts: "Ничего не найдено.",
    pickFacility: "Выберите площадку",
    noEffectiveBom:
      "Для этого продукта на этой площадке сегодня нет действующей спецификации — активируйте её в каталоге или закрепите версию выше.",
    noEffectiveRouting:
      "Для этого продукта на этой площадке сегодня нет действующего маршрута — активируйте его в каталоге или закрепите версию выше.",
    versionsTitle: "Закреплённые версии",
    routing: "Маршрут",
    activeVersion: "Действующая",
    overrideVersion: "Выбрать другую версию",
    versionOption: "Версия {{version}}",
    versionWarning:
      "Это не действующая версия — заказ будет построен по старой редакции. Убедитесь, что так и задумано.",
    pickFacilityFirst: "Сначала выберите площадку — версии закрепляются по площадке.",
    qty: "Количество",
    qtyInvalid: "Укажите количество больше нуля, не более трёх знаков после запятой.",
    uom: "Единица",
    dueDate: "Срок",
    priority: "Приоритет",
    notes: "Примечания",
    departmentOptional: "Необязательно",
    submitCreate: "Создать черновик",
    created: "Черновик создан.",
    atpTitle: "Доступность по компонентам",
    atpOk: "В наличии",
    atpShort: "Дефицит {{qty}}",
    atpUnknown: "Доступность неизвестна",
    atpAdvisory:
      "Предварительная оценка — юридически значим список дефицита, записанный при выдаче.",
    releaseSubmit: "Выдать в цех",
    releaseLater: "Выдать позже",
    releaseAnywayTitle: "Выдать с дефицитом?",
    releaseAnywayBody:
      "Дефицит по компонентам: {{count}}. При выдаче дефицит будет записан в заказ.",
    releaseAnyway: "Выдать всё равно",
    released: "Выдан в цех.",
    shortagesTitle: "Дефицит, записанный при выдаче",
    colRequired: "Требуется",
    colAvailable: "В наличии",
    colShortfall: "Дефицит",
    done: "Готово",
    pegBanner: "Под заказ клиента {{order}} · строка {{line}}",
    pegLoading: "Загружаем заказ клиента…",
    pegError: "Не удалось загрузить заказ клиента, под который создаётся заказ на производство.",
    pegBackToOrder: "Открыть заказ клиента",
    pegProductLocked: "Задан строкой заказа клиента.",
    releaseOrderFacilityOutOfScope:
      "Заказ клиента отгружается с площадки вне вашего доступа. Выдать этот черновик должен планировщик этой площадки.",
    permissionDenied: "У вас нет прав на это действие.",
  },

  dispatch: {
    title: "Диспетчерская доска",
    boardAria: "Диспетчерская доска",
    facilitiesError: "Не удалось загрузить список площадок.",
    pickFacilityFirst: "Выберите площадку, чтобы увидеть её рабочие центры.",
    pickDepartmentFirst: "Выберите подразделение, чтобы расположить его рабочие центры.",
    noWorkCenters: "В этом подразделении нет рабочих центров.",
    emptyColumn: "На этом рабочем центре очередь пуста.",
    columnError: "Не удалось загрузить очередь этого рабочего центра.",
    stationAbsent: "Этого рабочего центра больше нет.",
    qtyOf: "{{reported}} из {{planned}}",
    dueOn: "Срок {{date}}",
    priorityToast: "Приоритет установлен: {{value}}. Порядок очереди учитывает его.",
    crossColumnRefused:
      "Перенос задания между рабочими центрами — это планирование, его решает график в модуле планирования.",
    scheduleUpdating: "График обновляется",
    readOnlyPhone:
      "С телефона — только просмотр; менять приоритет перетаскиванием можно с компьютера.",
    dragHint: "Перетащите внутри колонки, чтобы задать приоритет (1–5).",
    cardHoldBadge: "Задержано ОТК",
    cardReworkBadge: "Переделка",
    cardBlockedAria: "Операция заблокирована",
  },

  // ---- S13 — заблокированные рабочие центры ---------------------------------
  //
  // «Простой» — слово, которым в цехе называют остановку оборудования; ровно
  // его печатает и сменный отчёт. «Задержка ОТК на операции» — полная фраза
  // (D-123, п. 9): в системе три разных «задержки», и рядом с карантинной
  // доской короткое слово читается неоднозначно.
  blocked: {
    title: "Остановленные рабочие центры",
    tableCaption: "Рабочие центры, которые не работают, и причина",
    colBlockedBy: "Что мешает",
    colWhy: "Причина",
    colFor: "Длительность",
    colWaiting: "Ждёт в очереди",
    qualityHoldChip: "Задержка ОТК на операции",
    downtimeChip: "Простой",
    triggersMaintenance: "Поломка",
    heldStep: "{{wo}}, операция {{seq}} — не прошла контроль",
    heldWhereToLook: "Забракованная проверка — на карантинной доске.",
    since: "с {{time}}",
    waitingCount: "Ждёт: {{count}}",
    waitingUnknown: "Очередь недоступна",
    nextUp: "Следующий: {{wo}} · {{product}}",
    openQueue: "Открыть очередь рабочего центра",
    openWorkOrder: "Открыть заказ",
    openQuarantine: "Открыть карантинную доску",
    emptyTitle: "Ничего не заблокировано",
    emptyBody:
      "Открытых простоев нет, задержек ОТК на операциях нет — проверено активных рабочих центров: {{count}}.",
    emptyBodyAllFacilities:
      "Открытых простоев нет ни на одной доступной вам площадке. Выберите площадку, чтобы проверить ещё и задержки ОТК.",
    scopeFacility: "Проверено активных рабочих центров: {{checked}} · заблокировано: {{blocked}}.",
    scopeAllFacilities:
      "Заблокировано: {{blocked}}. Показаны открытые простои по всем площадкам — выберите площадку, чтобы увидеть и задержки ОТК.",
    scopeUnreadable: "Не удалось прочитать рабочих центров: {{count}} — они не учтены.",
    overflow:
      "Открытых простоев больше, чем {{count}} — список неполный. Ограничьте выборку площадкой.",
    error: "Не удалось загрузить открытые простои",
    forbiddenTitle: "Доска простоев вам недоступна",
    forbiddenBody:
      "Простои и телеметрию оборудования видят администраторы площадки, планировщики и служба обслуживания. Обратитесь к администратору группы.",
    offlineTitle: "Нет сети — доска приостановлена",
    offlineBody: "Она заполнится сама, как только связь восстановится.",
    // §5 e35 — завершение простоя из офиса (D-299, решение H).
    end: {
      action: "Завершить простой",
      title: "Завершить этот простой",
      subject: "{{reason}} — открыт с {{time}}.",
      unknownReason: "Причина без названия",
      noteLabel: "Почему простой завершается здесь",
      endedAtLabel: "Работает снова с — не обязательно",
      endedAtHint:
        "Оставьте пустым, чтобы завершить сейчас. Если станок заработал раньше, укажите это время.",
      confirm: "Завершить простой",
      ending: "Завершаем…",
      noteRequired: "Укажите, почему простой завершается из офиса.",
      noteTooLong: "Сократите комментарий до {{max}} символов.",
      endAtNotAfterStart: "Станок не может заработать раньше, чем остановился в {{time}}.",
      endAtInFuture: "Это время ещё не наступило. Укажите, когда станок заработал.",
      endAtUnparseable: "Не удалось прочитать время. Выберите его снова или оставьте поле пустым.",
      endAtBeforeWindow:
        "Это больше 24 часов назад. Укажите время позже {{time}} или оставьте поле пустым, чтобы завершить простой сейчас.",
      endAtBeforeWindowNoTime:
        "Это больше 24 часов назад. Офис может записать окончание не более чем на 24 часа назад.",
      alreadyEnded: "Этот простой уже завершён в {{time}}. Доска обновляется.",
      alreadyEndedNoTime: "Этот простой уже завершён. Доска обновляется.",
      notFound: "Этого простоя больше нет в доступных вам данных. Обновите доску.",
      error: "Не удалось завершить простой",
      done: "Простой завершён в {{time}}.",
      openHeldOrder: "Открыть заказ, который он задерживал",
    },
    elapsed: {
      underMinute: "только что",
      // D-030: счётное существительное не согласуется с числом — «45 мин».
      minutes: "{{m}} мин",
      hours: "{{h}} ч",
      hoursMinutes: "{{h}} ч {{m}} мин",
      days: "{{d}} сут",
      daysHours: "{{d}} сут {{h}} ч",
    },
  },

  // ---- S7 — конфликты синхронизации -----------------------------------------
  conflicts: {
    title: "Конфликты синхронизации",
    // D-299, решение R1: исправление по ЗАВЕРШЁННОМУ заказу меняет операцию,
    // но не то, что заказ уже оприходовал на склад; исправить склад можно
    // только корректировкой остатков (модуль 06), решение за человеком.
    receiptUnchanged: {
      corrected:
        "Исправление применено: по операции теперь {{good}} {{uom}} годной продукции и {{scrap}} {{uom}} брака.",
      receipt:
        "Заказ уже завершён и оприходовал продукцию по этой операции, поэтому оприходование не изменилось: {{receipted}} {{uom}} годной продукции.",
      remedy:
        "Если остатки теперь неверны, исправьте их корректировкой остатков в разделе «Склад».",
      openOrder: "Открыть заказ",
    },
    error: "Не удалось загрузить очередь конфликтов",
    emptyOpenTitle: "На рассмотрении ничего нет",
    emptyOpenBody:
      "Записи из цеха, которые не удалось принять как есть или которые нужно перепроверить, ждут решения здесь.",
    emptyTitle: "Конфликтов нет",
    emptyBody: "По этим фильтрам конфликтов нет.",
    mobileHint: "С телефона решения недоступны — используйте компьютер.",
    filter: {
      status: "Статус",
      kind: "Тип",
      all: "Все",
      resolved: "Разрешённые",
    },
    status: { open: "Открытые" },
    rowStatus: {
      open: "Открыт",
      applied_with_edit: "Применён с правкой",
      discarded: "Отклонён",
      acknowledged: "Принят к сведению",
    },
    kind: {
      rejection: "Отклонённое событие",
      review_flag: "Пометка на проверку",
    },
    col: {
      when: "Когда",
      reason: "Причина",
      kind: "Тип",
      event: "Событие",
      status: "Статус",
    },
    payload: "Содержимое события",
    applyWithEdit: "Применить с правкой",
    discard: "Отклонить",
    acknowledge: "Принять к сведению",
    correctedPayload: "Исправленные данные (JSON)",
    badJson: "Это не корректный JSON.",
    note: "Примечание (необязательно)",
    noteRequired: "Примечание (обязательно при отклонении)",
    discardWarning:
      "Это событие не было применено — при отклонении пропадёт единственная запись о нём из цеха. Укажите причину: она останется в журнале.",
    confirmResolve: "Решить",
    resolveFailed: "Решение отклонено.",
    endBeforeWindow:
      "Планшет завершил этот простой более 24 часов назад — так давно записать нельзя. Отклоните с примечанием, затем завершите простой на доске остановленных рабочих центров.",
    endAtInvalid:
      "Время окончания простоя с планшета применить нельзя. Отклоните с примечанием, затем завершите простой на доске остановленных рабочих центров.",
    startBeforeWindow:
      "Планшет начал этот простой более 24 часов назад — так давно записать нельзя. Ничего не изменено. Отклоните с примечанием.",
    pauseStartBeforeWindow:
      "Планшет приостановил этот шаг более 24 часов назад — так давно записать нельзя. Удаление причины простоя не поможет. Отклоните с примечанием.",
    laborBeforeWindow:
      "Учёт труда с планшета по этому шагу старше 24 часов — так давно записать нельзя. Ничего не изменено. Отклоните с примечанием.",
    completeStoppageBeforeWindow:
      "Это завершение старше 24 часов, шаг остаётся приостановленным. Отклоните с примечанием, затем нажмите «{{record}}» на вкладке «{{tab}}» и отметьте «{{complete}}».",
    completeLaborBeforeWindow:
      "Это завершение старше 24 часов, шаг не завершён. Отклоните с примечанием, затем нажмите «{{record}}» на вкладке «{{tab}}» и отметьте «{{complete}}».",
    noNewQuantity: "Новое количество вводить не нужно: количество по шагу уже записано.",
    completeStoppageWayOn:
      "Укажите окончание в пределах последних 24 часов или сначала завершите простой на доске остановленных рабочих центров.",
    resolutionRejected: {
      rejection:
        "Это событие больше неприменимо, поэтому ничего не изменено. Отклоните этот конфликт и укажите причину в примечании.",
      review_flag:
        "Это исправление больше неприменимо, поэтому ничего не изменено. Примите пометку к сведению, чтобы оставить уже применённые значения.",
    },
    alreadyResolved:
      "Этот конфликт уже разрешил кто-то другой, поэтому ваш ввод не применён. Очередь обновлена.",
    wayOn: {
      returnAfterClose:
        "Заказ закрыли до поступления этого возврата, поэтому материал не принят. Отклоните с примечанием, затем оформите корректировку остатков в разделе «Склад».",
      rejectedAfterClose:
        "Заказ закрыт, поэтому возврат применить нельзя. Отклоните с примечанием, затем оформите корректировку остатков в разделе «Склад».",
    },
    awaiting: {
      note: "Это окончание ждёт своего начала, которое тоже отклонено. Сначала разрешите начало; если отклоните его, отклоните и это окончание.",
      rejected:
        "Это окончание всё ещё ждёт своего начала в этой очереди. Ничего не изменено. Разрешите начало, затем примените окончание снова.",
      showStart: "Показать начало",
      startElsewhere:
        "Начала нет на этой странице. Если оно применено, примените это окончание; если отклонено — отклоните и окончание.",
    },
    returnUnitNotAvailable:
      "Исходный контейнер сейчас не может принять материал: задержан, в пути или опустошён. Примените снова после освобождения или отклоните с примечанием.",
    correctionMismatch: {
      rejection:
        "Под этим исправлением уже записано другое событие (отличается: {{fields}}). Ничего не изменено. Отклоните, указав причину в примечании.",
      review_flag:
        "Под этим исправлением уже записано другое событие (отличается: {{fields}}). Ничего не изменено. Примите пометку к сведению, чтобы оставить применённые значения.",
    },
    mismatchField: {
      event_type: "тип события",
      worker_id: "работник",
      work_center_id: "рабочий центр",
      wo_operation_id: "шаг",
    },
    nextPage: "Следующая страница",
    prevPage: "Назад",
    reason: {
      invalid_transition: "Недопустимо из текущего состояния операции",
      qty_exceeds_remaining: "Заявленное количество превышает остаток",
      op_already_completed: "Операция уже завершена",
      wo_cancelled: "Заказ отменён",
      stock_insufficient: "Недостаточно запаса для выдачи",
      unknown_reference: "Ссылку не удалось распознать",
      stale_snapshot: "Часы устройства расходятся более чем на 24 ч",
      // «Блокировка», НЕ «карантин» (2026-08-20, задача #112): карантин — это
      // запись модуля 07 о ПАРТИИ/БОБИНЕ, а здесь речь о флаге НА ОПЕРАЦИИ
      // (spec 07 §2.7, D-123 п. 9 — три разные сущности, три разных слова).
      // Английская строка всегда говорила "quality hold"; русская брала чужое
      // слово, и именно на том экране, где рядом видна карантинная доска.
      qc_hold: "Операция заблокирована по качеству",
      worker_not_authenticated: "Работник не совпал с активной сессией",
      unsupported_event_kind: "Устройство отправило действие, которое система не обрабатывает",
    },
  },

  // ---- S8 — цеховые устройства ----------------------------------------------
  devices: {
    title: "Цеховые устройства",
    register: "Зарегистрировать устройство",
    registerFailed: "Регистрация отклонена.",
    error: "Не удалось загрузить реестр устройств",
    emptyTitle: "Устройств нет",
    emptyBody:
      "Зарегистрируйте планшеты, с которых цех отправляет события, — незарегистрированное устройство синхронизироваться не может.",
    mobileHint: "С телефона изменения реестра недоступны — используйте компьютер.",
    showRetired: "Показывать выведенные из эксплуатации",
    col: {
      code: "Код",
      name: "Название",
      lastSeen: "Последняя синхронизация",
      pending: "В очереди на устройстве",
      status: "Статус",
    },
    codeNote: "Напечатан на самом устройстве; после регистрации не меняется.",
    pendingHint: "В очереди: {{count}} (по данным устройства)",
    neverSeen: "Ещё не синхронизировалось",
    active: "Действует",
    retired: "Выведено",
    rename: "Переименовать",
    retire: "Вывести из эксплуатации",
    retireConfirm: "Вывести — записанные события останутся",
  },

  calibration: {
    title: "Дрейф калибровки",
    pickWorkCenterTitle: "Выберите рабочий центр",
    pickWorkCenterBody:
      "Дрейф оценивается по рабочему центру — это машинная единица, пока нет реестра оборудования.",
    facility: "Площадка",
    error: "Не удалось загрузить серию дрейфа",
    chartTitle: "Средняя дневная дисперсия, %",
    chartAria: "График дрейфа: средняя дневная дисперсия в процентах с полосой мин/макс",
    bucketDay: "По дням",
    bucketWeek: "По неделям",
    thresholdGuide: "Ориентир ±{{pct}}%",
    bandLegend: "полоса мин/макс",
    meanLabel: "Среднее за 30 дней",
    stddevLabel: "Разброс за 30 дней (σ)",
    noBarrelsValue: "Не измерялось",
    barrelsLabel: "Барабанов в окне",
    impactLabel: "Влияние на деньги (30 дней)",
    thresholdsLabel: "Пороги",
    thresholdsValue: "±{{mean}}% среднее · {{stddev}}% разброс",
    thresholdsStandard:
      "Стандарты платформы, одинаковые на всех площадках — площадка не может ослабить порог, по которому проверяется её собственный прибор.",
    statusOk: "В пределах порогов",
    statusBelowFloor: "Не оценивается — в окне меньше {{count}} барабанов",
    classification: {
      calibration_bias:
        "Систематическое смещение — устойчивый сдвиг; откалибруйте машину (её ошибка хотя бы предсказуема).",
      meter_unreliable:
        "Счётчик ненадёжен — ошибка меняется от барабана к барабану; на маршрутных шагах этого центра принудительно включён контрольный замер.",
    },
    emptyTitle: "Записей калибровки нет",
    emptyBody:
      "Записи появляются, когда рабочие завершают шаги с контрольным замером на этом центре — показание машины и измеренное, барабан за барабаном.",
    recentTitle: "Последние барабаны",
    csvExport: "Экспорт CSV",
    col: {
      measuredAt: "Замер",
      batch: "Барабан",
      claimed: "Показание машины",
      measured: "Измерено",
      variance: "Дисперсия",
      variancePct: "Дисперсия, %",
      method: "Метод",
      worker: "Рабочий",
    },
    method: {
      scale_weight: "Весы",
      meter_counter: "Счётчик метров",
      manual_tape: "Рулетка",
    },
    tabEmptyTitle: "По этому заказу записей калибровки нет",
    tabEmptyBody:
      "Шаги с контрольным замером записывают показание машины и измеренное при завершении — пока ничего не записано.",
    tabError: "Не удалось загрузить записи калибровки",
  },

  // ---- S9/S10 — экраны цеха -------------------------------------------------
  // ---- S5 — табло смены (§4 S5, slice 04-C-4) -----------------------------
  shifts: {
    title: "Табло смены",
    facility: "Площадка",
    shiftDate: "Дата смены",
    // The pack's shift codes as a person says them (D-455); an unknown
    // code falls back to itself.
    shiftNames: {
      DAY: "День",
      NIGHT: "Ночь",
      MORNING: "Утро",
      AFTERNOON: "День (вторая)",
      EVENING: "Вечер",
      OFFICE: "Офисные часы",
    },
    shiftDateNote:
      "Дата НАЧАЛА смены. Ночная смена, переходящая за полночь, ищется по дню, в который началась.",
    tableCaption: "Количество, выход с первого предъявления и простой по сменам и участкам",
    colShift: "Смена",
    colGood: "Годное",
    colScrap: "Брак",
    colFpy: "Выход с первого раза",
    colDowntime: "Простой",
    colHandedOff: "Передано брака",
    colFlagged: "Отмечено брака",
    colWorkers: "Рабочих",
    colState: "Состояние",
    totalGood: "Годное за день",
    totalFpy: "Выход с первого раза",
    totalDowntime: "Простой",
    minutes: "{{n}} мин",
    noYield: "—",
    worstBadge: "Худший выход",
    stateLive: "Идёт",
    stateFinalized: "Закрыта",
    emptyTitle: "За эту дату смены ничего не записано",
    emptyBody:
      "Табло наполняется по мере отчётов цеха о количестве. Проверьте дату или выберите другую площадку.",
    error: "Не удалось загрузить табло",
    defectsPending:
      "Брак засчитывается смене, которая его произвела (даже закрытой), и смене, которая его отметила.",
    defectsScrapMoves: "Решение «в брак» переносит количество в брак и выход производившей смены.",
    handoffTitle: "Передача брака",
    handoffPending:
      "За эту дату смены передач брака нет. Передача появляется, когда рабочий отмечает брак предыдущей смены.",
    handoffError: "Не удалось загрузить журнал передач",
    handoffCaption: "Брак, отмеченный одной сменой против другой, и принятые решения",
    handoffColProduced: "Произвела",
    handoffColFlagged: "Отметила",
    handoffColQty: "Количество",
    // «Причина брака» — код причины из каталога отходов (модуль 05), НЕ
    // обоснование решения ниже. Оба поля назывались «Причина» в одной таблице,
    // хотя в английском они различаются («Reason» / «Why»).
    handoffColReason: "Причина брака",
    handoffColState: "Решение",
    handoffShiftCell: "{{code}} · {{date}}",
    handoffWorkerUnknown: "Бригада — один рабочий не определён",
    handoffDisposition: {
      pending: "Не решено",
      rework: "В переделку",
      scrap: "В брак",
      accepted_as_is: "Принято как есть",
    },
    handoffRework: "В переделку",
    handoffScrap: "В брак",
    handoffAccept: "Принять как есть",
    // «Карантин» — слово модуля 07 для записи о партии (D-123, решение 9).
    // «В брак» — с оговоркой: барьер C6 отказывает, если качество уже списало
    // брак этой операции (D-299, решение F2). Фраза о партиях — только для
    // ЗАВЕРШЁННОГО заказа; отменённый ничего не оприходовал (решение F3).
    handoffReworkStock:
      "Чтобы переделать продукцию, уже оприходованную на склад, запланируйте новую работу по её партиям или поместите их в карантин на карантинной доске.",
    handoffReworkBlockedNoStock:
      "Заказ в статусе «{{status}}», поэтому шаг нельзя открыть для переделки. Примите как есть или отправьте в брак, если ОТК ещё не списал его.",
    // D-299, решения F1/F2: фраза под ОТКЛОНЁННЫМ решением — из списка
    // `available_dispositions` самого отказа; {{decisions}} — список через «или».
    handoffRefusedNotLive:
      "Статус заказа — «{{status}}», поэтому операцию уже нельзя открыть заново для переделки. Остаётся: {{decisions}}.",
    handoffRefusedScrapAttributed:
      "Качество уже списало брак этой операции — списание здесь учло бы потерю дважды. Остаётся: {{decisions}}.",
    // Закрытый заказ не принимает ни одного решения (§9.4).
    handoffOrderClosed:
      "Статус заказа — «{{status}}», поэтому решение по этой передаче больше записать нельзя.",
    // Обоснование РЕШЕНИЯ, а не причина брака (см. handoffColReason выше).
    handoffNoteLabel: "Обоснование",
    handoffNoteHint:
      "Обязательно для решения «в брак» — количество переносится в выход производившей смены.",
    handoffDeciding: "Сохранение…",
    handoffDecideError: "Не удалось записать решение",
    handoffPhotos: "Фото: {{n}}",
  },

  // ---- S14 — сдельные записи (/production/piece-rate) -----------------------
  // Три отсутствия, и каждое — фраза, а не пустая клетка: деньги скрыты ПО
  // ПРАВАМ (§7 — ключа нет вовсе), деньги пусты ПО ЗРЕЛОСТИ (§12 Q1 — таблицы
  // расценок нет) и строк нет вообще, потому что ни один шаг маршрута не
  // отмечен как сдельный (D-118: неработающее говорит, ПОЧЕМУ именно).
  pieceRate: {
    title: "Сдельные записи",
    tableCaption: "Сдельное количество по рабочим, сменам и шагам",
    colShiftDate: "Дата смены",
    colWorker: "Рабочий",
    colFacility: "Площадка",
    colOperation: "Шаг",
    colQty: "Сдельное количество",
    colRate: "Расценка",
    colAmount: "Сумма",
    colStatus: "Состояние",
    colReported: "Отчёт",
    dateFrom: "Дата смены с",
    dateTo: "Дата смены по",
    statusFilter: "Состояние",
    statusAny: "Любое состояние",
    statusCaptured: "Записано",
    statusPriced: "Оценено",
    statusSettled: "Выплачено",
    shiftDateNote:
      "Дата начала смены: ночная смена после полуночи учитывается и оплачивается по дню, когда началась.",
    loadMore: "Показать ещё",
    error: "Не удалось загрузить сдельные записи",
    unpricedNote: "Расценка к этим количествам не применялась.",
    moneyHiddenNote:
      "Расценки и суммы вашей роли не показываются. Здесь виден только зафиксированный объём; за суммами обратитесь к администратору группы.",
    moneyPartialNote:
      "Расценки и суммы скрыты: часть записей относится к другим рабочим. Отфильтруйте свои записи, чтобы увидеть суммы.",
    emptyTitle: "Сдельное количество не зафиксировано",
    emptyBody: "Записи появляются, когда цех сообщает годное количество на сдельном шаге.",
    emptyFilteredTitle: "По этим фильтрам записей нет",
    emptyFilteredBody:
      "Расширьте даты смен, выберите другую площадку или снимите фильтр по состоянию.",
    forbiddenTitle: "Журнал сдельных записей вам недоступен",
    forbiddenBody:
      "Сдельные количества доступны финансам, администраторам площадок и группы; рабочий видит свои. Обратитесь к администратору группы, если они вам нужны.",
    offlineTitle: "Нет сети — журнал приостановлен",
    offlineBody: "Он заполнится сам, как только связь вернётся.",
  },

  floor: {
    // ---- Плитки модуля 04 на главном экране цеха ----------------------------
    // (аудит смены оператора, 2026-08-17: у выбора рабочего центра и «моей
    // смены» не было ни одного входа с экрана — только прямой URL или QR.)
    // «Рабочий центр», не «станция» — тот же термин, что во всём цеховом
    // словаре ниже (picker/station).
    home: {
      station: "Мой рабочий центр",
      stationSub: "Откройте очередь работ — выберите рабочий центр из списка, QR не нужен.",
      myShiftSub: "Ваш итог за сегодня — годное, брак, завершённые операции.",
    },
    // ---- S11 «моя смена» + S12 настенное табло (04-C-4) ---------------------
    back: "Назад",
    myShift: {
      title: "Моя смена",
      shiftLine: "{{code}} · {{date}}",
      noShift: "Сейчас смена не идёт.",
      good: "Годное",
      scrap: "Брак",
      ops: "Операций завершено",
      pieceQty: "Сдельное количество",
      rateNote: "Только количество, без суммы оплаты.",
      errorTitle: "Не удалось загрузить вашу смену",
      errorBody: "Ничего из записанного не потеряно. Нажмите «Повторить» или проверьте связь.",
      offlineWhat: "итог вашей смены",
    },
    wall: {
      title: "Табло смены",
      fpyLabel: "С первого раза",
      noYield: "—",
      worst: "Худший",
      scope: "Смена {{shift}} · {{date}}",
      live: "ОНЛАЙН",
      stale: "УСТАРЕЛО",
      stateOpen: "Открыта",
      stateClosed: "Закрыта",
      outputLabel: "Выпуск годного",
      downtime: "Простой {{n}} мин",
      footScrap: "Брак",
      footRework: "Доработка",
      footWorkers: "Рабочих",
      totalOutput: "Выпуск завода",
      totalYield: "С первого раза",
      totalScrap: "Брак",
      totalRework: "Доработка",
      totalDowntime: "Простои",
      minutesUnit: "мин",
      totalsMixed: "Итоги по цеху скрыты, пока на доске две даты ({{dates}}).",
      updatedAgoSeconds: "Обновлено {{n}} с назад",
      updatedAgoMinutes: "Обновлено {{n}} мин назад",
      updatedAgoHours: "Обновлено {{n}} ч назад",
      neverLoaded: "Ожидание первого обновления…",
      empty: "По этой смене пока ничего не отчитано.",
      more: "+{{n}} участков ещё",
    },
    // Рядом со статусом, когда ЭФФЕКТИВНЫЙ статус (снимок + нажатия на этом
    // устройстве, §3.4) отличается от серверного снимка.
    pendingSync: "ожидает синхронизации",
    picker: {
      title: "Выберите рабочий центр",
      scanLabel: "Отсканируйте QR рабочего центра",
      scanPlaceholder: "Отсканируйте или введите код…",
      scanMiss: "Это не QR рабочего центра и не QR операции.",
      resume: "Вернуться: {{name}}",
      errorTitle: "Не удалось загрузить рабочие центры",
      errorBody: "Для первого открытия списка нужна связь.",
      emptyTitle: "Рабочих центров нет",
      emptyBody: "На этой площадке ещё нет рабочих центров — их заводит офис.",
      open: "Открыть этот рабочий центр",
    },
    station: {
      title: "Рабочий центр",
      changeStation: "Сменить рабочий центр",
      what: "очередь этого рабочего центра",
      needsReview: "Требуют проверки: {{count}} — откройте лоток",
      shiftLeft: "Осталось {{minutes}} мин",
      myOpen: "Мои открытые операции",
      queue: "Очередь",
      emptyTitle: "На этом рабочем центре очередь пуста.",
      emptyBody: "Выданные в цех работы для этого центра появятся здесь.",
      errorTitle: "Не удалось загрузить рабочий центр",
      errorBody: "Очередь не загрузилась — нажатия всё равно копятся на устройстве.",
      progress: "{{reported}} из {{planned}}",
      openOp: "Открыть",
      reworkBadge: "Переделка",
      holdBadge: "Задержано ОТК",
      noDevices:
        "На этой площадке нет зарегистрированных цеховых устройств, отправка невозможна — попросите офис зарегистрировать этот планшет (Производство → Устройства).",
      device: "Это устройство: {{code}} · {{name}}",
      changeDevice: "Сменить",
      pickDevice: "Какое это устройство?",
      pickDeviceBody:
        "Выберите код, напечатанный на этом планшете, — им помечается всё, что вы записываете.",
      thisDevice: "Это устройство",
    },
    op: {
      title: "Операция",
      what: "эту операцию",
      notHereTitle: "Не на этом рабочем центре",
      notHereBody:
        "Этой операции нет в очереди закреплённого рабочего центра. Отсканируйте QR у её рабочего центра.",
      staleIdentity:
        "Данные рабочего места на этом планшете устарели. Подключитесь к сети один раз, чтобы обновить их, — до этого ваши нажатия не записываются.",
      blockedIdentity: "Сначала поймайте связь — планшет не знает, кто работает",
      blockedDevice: "Выберите вверху экрана, что это за планшет",
      blockedSending: "Отправляем…",
      pendingHere: "Нажатий по этой операции ещё на устройстве: {{count}}.",
      applied: "Записано.",
      queued: "В очереди на устройстве — отправится при появлении связи.",
      conflicted: "Требует проверки — {{reason}}. Решение принимает офис в очереди конфликтов.",
      openTray: "Открыть лоток",
      needsAttention: "Это нажатие не отправилось — оно ждёт в лотке.",
      doneNote: "Эта операция здесь завершена.",
      start: "Старт",
      pause: "Пауза",
      resume: "Продолжить",
      complete: "Завершить",
      guardNoQty: "Сообщите количество, прежде чем нажать «Завершить».",
      guardCalibration: "Запишите калибровку ниже, прежде чем нажать «Завершить».",
      guardSerial: "Отсканируйте или введите серийный номер ниже, прежде чем нажать «Завершить».",
      guardQcHold: "Операция задержана ОТК. Попросите офис или контролёра ОТК снять задержку.",
      reworkNote:
        "Операция открыта заново для переделки — то, что вы заявите здесь, учитывается как переделка, а не как новый выпуск.",
      reportQty: "Заявить количество",
      qtyGood: "Годных",
      qtyScrap: "Брак",
      scrapReason: "Причина брака",
      noScrapReasons: "В справочнике нет причин брака — их ведёт офис в настройках отходов.",
      nothingReported: "Сначала укажите количество годных или брака.",
      reasonRequired: "Выберите причину брака.",
      reportButton: "Заявить",
      qtyIdentity: "Количество",
      // Цифровая клавиатура S10 (§4 строка 10, D-091).
      numpadOpen: "Клавиатура",
      numpadDone: "Готово",
      numpadBackspace: "Стереть последнюю цифру",
      numpadDecimal: "Десятичный разделитель",
      materialIdentity: "Материал",
      material: "Материал",
      materialLine: "Выдано {{issued}} из {{planned}} {{uom}}",
      issueNoScan: "Выдать без сканирования",
      noMaterials: "Для этой операции материалов по плану нет.",
      scanLabel: "Отсканируйте QR партии или единицы",
      scanPlaceholder: "Отсканируйте этикетку материала…",
      scanTarget: "Отсканировано: {{code}}",
      manualTarget: "Выдача: {{name}}",
      // D-081 (04-B-4): возврат — обратная операция к выдаче по скану, на том
      // же экране; знак ставит режим.
      materialMode: "Выдача или возврат",
      modeIssue: "Выдача",
      modeReturn: "Возврат",
      returnNoScan: "Вернуть без сканирования",
      scanReturnTarget: "Возврат по скану: {{code}}",
      manualReturnTarget: "Возврат: {{name}}",
      returnButton: "Вернуть на склад",
      returnIdentity: "Возврат материала",
      materialQty: "Количество",
      uom: "Единица",
      noUom: "Для этого скана единица измерения неизвестна — выдайте по плановой строке.",
      materialNeedsQty: "Сначала укажите количество",
      issueButton: "Выдать",
      // 04-C-1: кнопка простоя + сетка причин + индикатор открытого простоя.
      downtime: "Простой",
      downtimeButton: "Отметить простой",
      downtimeIdentity: "Простой",
      downtimeEndIdentity: "Окончание простоя",
      downtimeOpenNote: "Открытых интервалов простоя на этом посту: {{count}}.",
      downtimeSince: "Простой с {{time}}",
      downtimeEnd: "Завершить простой",
      downtimeNoEnd: "Открыт из офиса — завершается там же.",
      downtimeNoReasons:
        "В каталоге нет активных причин простоя — офис управляет ими в разделе «Причины простоя».",
      flagDefect: "Отметить брак",
      defectQty: "Количество брака",
      defectProducedAt: "На какой операции сделано?",
      defectProducedHere: "На этой операции",
      defectProducedHereOnly:
        "Будет записано на эту операцию. Операцию другого станка укажите в примечании.",
      defectNotes: "Примечание (необязательно)",
      defectSend: "Отметить",
      defectIdentity: "отметка брака",
      defectNothingFlagged: "Укажите, сколько бракованного.",
      defectPhoto: "Фото-доказательство",
      defectPhotoAdd: "Добавить фото",
      defectPhotoAddAnother: "Добавить ещё фото",
      defectPhotoRetake: "Переснять",
      defectPhotoPreview: "Фото-доказательство {{n}}",
      defectPhotoHint:
        "Необязательно, но именно фото решает спор между сменами. Оно сохраняется на планшете вместе с отметкой и загружается, когда появится связь.",
      defectPhotoTooLarge: "Фото больше лимита в 20 МБ — переснимите его.",
      defectPhotoWrongType:
        "Этот файл не является фотографией поддерживаемого формата (JPEG, PNG, WebP или HEIC).",
      defectPhotoLimit: "Не больше {{max}} фото на одну отметку.",
      cancel: "Отмена",
      calibration: "Калибровка",
      calibrationIdentity: "Калибровка",
      calibrationBatch: "Метка барабана / партии",
      calibrationBatchPlaceholder: "напр. B-0217",
      calibrationClaimed: "Показание машины",
      calibrationMeasured: "Измерено",
      calibrationUomPlaceholder: "напр. м",
      calibrationMethod: "Чем измерено?",
      calibrationMethods: {
        scale_weight: "Весы",
        meter_counter: "Счётчик метров",
        manual_tape: "Рулетка",
      },
      calibrationScan: "QR барабана (необязательно)",
      calibrationScanPlaceholder: "Отсканируйте этикетку выходного барабана…",
      calibrationProblem:
        "Нужны метка барабана, единица измерения и оба положительных количества — показание машины и ваш замер.",
      calibrationRecord: "Записать калибровку",
      // 04-C-3: форма фиксации серийного номера/партии (§3.3 шаг 8).
      serial: "Серийный номер / партия",
      serialIdentity: "Фиксация серийного номера",
      serialOpen: "Зафиксировать серийный номер или партию",
      serialDirection: "В какую сторону?",
      serialDirections: {
        produced: "Сошло с этого станка",
        consumed: "Пошло в этот станок",
      },
      serialDirectionNote: {
        produced: "Записывается то, что операция ВЫПУСТИЛА, — именно это нужно для завершения.",
        consumed: "Записывается то, что операция ИЗРАСХОДОВАЛА, — входная сторона цепочки.",
      },
      serialScanLabel: "Отсканируйте этикетку",
      serialScanPlaceholder: "Нажмите на сканере или введите код…",
      serialScanned: "Отсканировано: {{code}}",
      serialNo: "Гравировка (если кабель промаркирован)",
      serialNoPlaceholder: "например, KZ-2026-0042",
      serialQty: "Измеренная длина / вес",
      serialCapture: "Сохранить",
      serialProblem: {
        nothing_identified: "Отсканируйте этикетку или введите гравировку — одно из двух.",
        qty_required: "Укажите измеренное количество.",
        uom_required: "Укажите единицу измерения.",
        scan_in_serial:
          "В поле гравировки — отсканированная этикетка, не совпадающая с полем скана. Очистите одно поле: одна запись — одна катушка.",
      },
      scanUnresolved:
        "«{{code}}» не найден на складе завода. Проверьте этикетку или введите гравировку. Ваше действие сохранено для офиса —",
    },
    qr: {
      title: "QR операции",
      noStationTitle: "Рабочий центр не закреплён",
      noStationBody:
        "Сначала отсканируйте QR рабочего центра — он определяет, что может это устройство.",
      elsewhereTitle: "Не на этом рабочем центре",
      elsewhereBody:
        "Этой операции нет в очереди закреплённого рабочего центра. Отсканируйте QR у её рабочего центра.",
      noSnapshotTitle: "Без связи не определить",
      noSnapshotBody:
        "Подключитесь один раз у своего рабочего центра, чтобы загрузить очередь, и отсканируйте QR операции снова.",
    },
  },

  problem: {
    WORK_ORDER_NOT_FOUND: "Заказ на производство не найден.",
    WORK_ORDER_OPERATION_NOT_FOUND: "Такой операции в этом заказе нет.",
    MATERIAL_ISSUE_NOT_FOUND: "Выдача материала, которую нужно сторнировать, не найдена.",
    PRODUCT_NOT_FOUND: "Продукт не найден.",
    FACILITY_NOT_FOUND: "Площадка не найдена.",
    WORK_CENTER_NOT_FOUND: "Рабочий центр не найден.",
    BOM_NOT_FOUND: "Такая версия спецификации не найдена.",
    ROUTING_NOT_FOUND: "Такая версия маршрута не найдена.",
    NO_EFFECTIVE_BOM: "Для этого продукта на этой площадке сегодня нет действующей спецификации.",
    NO_EFFECTIVE_ROUTING: "Для этого продукта на этой площадке сегодня нет действующего маршрута.",
    WO_INVALID_TRANSITION: "Из текущего состояния заказа такой переход невозможен.",
    WO_NOT_EDITABLE:
      "Редактировать можно только черновик. Исключение — приоритет: диспетчерская доска меняет его и по ходу работ.",
    WO_CLOSED_IMMUTABLE: "Заказ закрыт — изменить в нём больше ничего нельзя.",
    WO_RELEASE_SHORTAGES_UNACKNOWLEDGED:
      "По компонентам есть дефицит — подтвердите выдачу, дефицит будет записан.",
    WO_COMPLETE_BLOCKED:
      "Есть операция, которая не завершена и не пропущена, — заказ нельзя завершить.",
    WO_CLOSE_BLOCKED: "По заказу ещё есть незакрытые события — закрыть его пока нельзя.",
    WO_CANCEL_DISPOSITION_REQUIRED:
      "Перед отменой укажите, что делать с произведённым количеством и выданным материалом.",
    WO_OUTPUT_APPORTIONMENT_INVALID:
      "Доли выходов в спецификации не дают в сумме 100% — пул затрат нельзя распределить.",
    OPERATION_NOT_ACTIONABLE: "В текущем состоянии шаг не принимает это действие.",
    OPERATION_END_AT_INVALID:
      "Время окончания в будущем, поэтому ничего не сохранено. Укажите, когда шаг действительно закончился.",
    OPERATION_START_AT_INVALID:
      "Время начала в будущем, поэтому ничего не сохранено. Укажите, когда шаг действительно начался.",
    MEASURED_CAPTURE_REQUIRED:
      "Для этого шага обязателен контрольный замер — запись калибровки (показание машины и измеренное) должна существовать до завершения. Цех записывает её на листе завершения.",
    SERIAL_CAPTURE_REQUIRED:
      "Этому шагу нужен серийный номер или партия до завершения. Отсканируйте в цеху или внесите гравировку с маршрутного листа вместе с результатом.",
    SCRAP_REASON_REQUIRED: "При любом браке причина обязательна.",
    SCRAP_REASON_UNKNOWN: "Такой причины брака нет в справочнике.",
    MATERIAL_RETURN_EXCEEDS_ISSUED:
      "Возврат больше, чем осталось выданным с учётом уже оформленных возвратов.",
    WO_OUTPUT_CURRENCY_MISMATCH:
      "Побочная продукция оценена в другой валюте, чем заказ, — зачесть её нельзя.",
    TRAVELER_GLYPH_COVERAGE:
      "В маршрутном листе есть символы, которые встроенные шрифты не могут напечатать.",
    ORDER_LINE_NOT_FOUND: "Такая строка заказа клиента не найдена.",
    ORDER_NOT_CONFIRMED:
      "Заказ клиента ждёт согласования. Планировать под него производство можно после подтверждения.",
    ORDER_TERMINAL:
      "Заказ клиента уже отгружен, доставлен, закрыт или отменён — планировать под него производство нельзя.",
    ORDER_LINE_TERMINAL:
      "Эта строка заказа клиента уже готова, отгружена или отменена — планировать под неё производство или выдавать заказ на производство в цех больше нельзя.",
    ORDER_LINE_PRODUCT_MISMATCH:
      "Строка заказа клиента продаёт другой продукт. Заказ на производство под неё должен выпускать именно его.",
    CREDIT_BLOCKED:
      "Заказ клиента на кредитном стопе, поэтому этот заказ нельзя выдать в цех. Выдайте его после снятия стопа или поступления предоплаты.",
    SHIPMENT_OPEN:
      "По этой строке заказа клиента собирается отгрузка. Отгрузите или отмените её, прежде чем выдавать заказ на производство в цех.",
    DOWNTIME_LOG_NOT_FOUND: "Такой простой не найден.",
    DOWNTIME_ALREADY_ENDED: "Этот простой уже завершён.",
    DOWNTIME_END_AT_INVALID:
      "Время окончания должно быть позже начала простоя, не позже текущего момента и не более чем на 24 часа назад.",
    UNIT_NOT_AVAILABLE:
      "Этот контейнер сейчас нельзя использовать для этого, поэтому ничего не изменено. Возможно, он задержан службой качества, находится в пути или пуст.",
    UNIT_NOT_FOUND: "Такой контейнер не найден.",
  },
};
